import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useNavigate } from 'react-router-dom';
import 'react-phone-input-2/lib/style.css';
import './DentalService.css';
import { FaPhone, FaWhatsapp } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const DentalServicePage = ({
    serviceName,
    serviceTagline,
    whatIsContent,
    whenRequiredContent,
    howDoneContent,
    benefitsContent,
    procedureSteps,
    seoData // New prop for SEO tags
}) => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        date: '',
        time: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleWhatsappButtonClick = () => {
        if (navigator.vibrate) {
            navigator.vibrate(15); // Subtle haptic feedback
        }
        window.open('https://wa.me/918076353837', '_blank');
    };

    const handlePhoneButtonClick = () => {
        if (navigator.vibrate) {
            navigator.vibrate(15); // Subtle haptic feedback
        }
        window.location.href = `tel:${918076353837}`;
    };
    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Name is required';

        // Make email optional but validate format if provided
        if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email address is invalid';
        }

        // Validate phone - using library's format so just check if it exists
        if (!formData.phone) {
            errors.phone = 'Phone number is required';
        } else if (formData.phone.length < 8) {
            errors.phone = 'Please enter a valid phone number';
        }

        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: null,
            });
        }
    };

    const handlePhoneChange = (value) => {
        setFormData({
            ...formData,
            phone: value,
        });

        // Clear error when user changes phone
        if (formErrors.phone) {
            setFormErrors({
                ...formErrors,
                phone: null,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();

        if (Object.keys(errors).length === 0) {
            setIsSubmitting(true);

            try {
                // Clear any previous submission tracking
                const submissionKey = `submission-${formData.name}-${formData.phone}`;
                const whatsappKey = `whatsapp-${formData.name}-${formData.phone}`;

                // Clear previous tracking for this user if it exists
                sessionStorage.removeItem(submissionKey);
                sessionStorage.removeItem(whatsappKey);

                // Fire GTM event for form submission start
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'form_submission_start',
                        formType: 'contact',
                    });
                }

                // Create a request to Google Apps Script
                const scriptURL =
                    'https://script.google.com/macros/s/AKfycbwNJdPuF7LgDL4fJYMPdZG7bZoyvzvVqc50iyuZL6P8q0HKyI20mrQgyyA8SS_NchC-hg/exec'; // Replace with your script URL

                // Send the form data as JSON
                const response = await fetch(scriptURL, {
                    method: 'POST',
                    body: JSON.stringify(formData),
                    headers: {
                        'Content-Type': 'text/plain;charset=utf-8',
                    },
                    mode: 'no-cors', // Important for handling CORS issues
                });

                // Navigate to thank you page with all form data
                navigate('/thank-you', {
                    state: {
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        message: formData.message,
                    },
                });
            } catch (error) {
                console.error('Error submitting form:', error);

                // Fire GTM event for form submission error
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'form_submission_error',
                        errorMessage: error.message || 'Unknown error occurred',
                    });
                }

                alert('There was an error submitting your message. Please try again later.');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setFormErrors(errors);

            // Fire GTM event for form validation error
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'form_validation_error',
                    formErrors: Object.keys(errors),
                    formType: 'contact',
                });
            }
        }
    };
    // Replace your current handleButtonClick function with this:
    const handleButtonClick = () => {
        navigate('/contact');
    };

    return (
        <div className='service-page'>
            {seoData && (
                <Helmet>
                    <title>{seoData.title}</title>
                    <meta name="description" content={seoData.description} />
                    {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
                    {seoData.canonical && <link rel="canonical" href={seoData.canonical} />}

                    {/* Open Graph / Facebook */}
                    {seoData.ogTitle && <meta property="og:title" content={seoData.ogTitle} />}
                    {seoData.ogDescription && <meta property="og:description" content={seoData.ogDescription} />}
                    {seoData.ogUrl && <meta property="og:url" content={seoData.ogUrl} />}

                    {/* Twitter */}
                    {seoData.twitterTitle && <meta name="twitter:title" content={seoData.twitterTitle} />}
                    {seoData.twitterDescription && <meta name="twitter:description" content={seoData.twitterDescription} />}
                </Helmet>
            )}
            {/* Hero Section */}
            <div className='hero-section'>
                <div className='specific-service-container'>
                    <h1>{seoData && seoData.h1Text ? seoData.h1Text : serviceName}</h1>
                    <p>{serviceTagline}</p>
                </div>
            </div>
            <div className={`mini-header visible`}>
                <div className='mini-header-logo'>Wisdom Dental</div>
                <button className='mini-header-cta' onClick={handleButtonClick}>
                    Book Now
                </button>
            </div>

            {/* What Section */}
            <section className='service-section'>
                <div className='specific-service-container'>
                    <h2 className='section-title'>What is {serviceName}?</h2>
                    <div className='section-content'>
                        <div className='section-text'>
                            {whatIsContent.map((paragraph, index) => (
                                <p key={index}>{paragraph}</p>
                            ))}
                        </div>
                        <div className='section-image'>
                            <img src='treatment.jpg' alt={`${serviceName} explanation`} />
                        </div>
                    </div>
                </div>
            </section>

            {/* When Required Section */}
            <section className='service-section alternate'>
                <div className='specific-service-container'>
                    <h2 className='section-title'>When is {serviceName} Required?</h2>
                    <div className='section-content'>
                        <div className='section-image'>
                            <img src='treatment.jpg' alt={`${serviceName} indications`} />
                        </div>
                        <div className='section-text'>
                            {whenRequiredContent.paragraphs &&
                                whenRequiredContent.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}

                            {whenRequiredContent.list && (
                                <>
                                    <p>{whenRequiredContent.listIntro || 'You may need this procedure if you experience:'}</p>
                                    <ul className='symptom-list'>
                                        {whenRequiredContent.list.map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {whenRequiredContent.closing && <p>{whenRequiredContent.closing}</p>}
                        </div>
                    </div>
                </div>
            </section>

            {/* How It's Done Section */}
            <section className='service-section'>
                <div className='specific-service-container'>
                    <h2 className='section-title'>How {serviceName} is Done at Wisdom Dental Clinic</h2>
                    <div className='section-content'>
                        <div className='section-text'>
                            {howDoneContent.paragraphs &&
                                howDoneContent.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}

                            {howDoneContent.list && (
                                <>
                                    <p>{howDoneContent.listIntro || 'Our clinic features:'}</p>
                                    <ul>
                                        {howDoneContent.list.map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {howDoneContent.closing && <p>{howDoneContent.closing}</p>}
                        </div>
                        <div className='section-image'>
                            <img src='treatment.jpg' alt='Advanced dental technology' />
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className='service-section alternate'>
                <div className='specific-service-container'>
                    <h2 className='section-title'>Benefits of {serviceName}</h2>
                    <div className='section-content'>
                        <div className='section-image'>
                            <img src='treatment.jpg' alt='Happy dental patient' />
                        </div>
                        <div className='section-text'>
                            <div className='benefits-grid'>
                                {benefitsContent.map((benefit, index) => (
                                    <div className='benefit-item' key={index}>
                                        <h3>{benefit.title}</h3>
                                        <p>{benefit.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Procedure Section */}
            <section className='service-section'>
                <div className='specific-service-container'>
                    <h2 className='section-title'>The {serviceName} Procedure</h2>
                    <div className='section-content'>
                        <div className='section-text'>
                            <div className='procedure-steps'>
                                {procedureSteps.map((step, index) => (
                                    <div className='step' key={index}>
                                        <div className='step-number'>{index + 1}</div>
                                        <div className='step-content'>
                                            <h3>{step.title}</h3>
                                            <p>{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className='section-image'>
                            <img src='treatment.jpg' alt={`${serviceName} procedure steps`} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className='contact-section'>
                <div className='specific-service-container'>
                    <div className='section-title-div'>
                        <h2 className='section-title'>Schedule Your Consultation</h2>
                    </div>
                    <div className='contact-intro'>
                        <p className='contact-intro'>
                            Have questions about {serviceName.toLowerCase()}? Want to schedule an appointment? Fill out the form below and
                            we'll get back to you promptly.
                        </p>
                    </div>
                    <div className='improved-contact-form'>
                        <form onSubmit={handleSubmit} className='improved-contact-form'>
                            <div className='improved-form-group'>
                                <label htmlFor='name'>
                                    Full Name <span className='required'>*</span>
                                </label>
                                <input
                                    type='text'
                                    id='name'
                                    name='name'
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={formErrors.name ? 'error' : ''}
                                    placeholder='Your full name'
                                />
                                {formErrors.name && <span className='error-message'>{formErrors.name}</span>}
                            </div>

                            <div className='improved-form-row'>
                                <div className='improved-form-group'>
                                    <label htmlFor='phone'>
                                        Phone Number <span className='required'>*</span>
                                    </label>
                                    <PhoneInput
                                        country={'in'}
                                        value={formData.phone}
                                        onChange={handlePhoneChange}
                                        inputProps={{
                                            id: 'phone',
                                            name: 'phone',
                                            required: true,
                                            className: formErrors.phone ? 'error' : '',
                                        }}
                                        containerClass='improved-phone-input-container'
                                        buttonClass={formErrors.phone ? 'country-dropdown error' : 'country-dropdown'}
                                        inputClass={formErrors.phone ? 'error' : ''}
                                        preferredCountries={['in', 'us', 'gb', 'ae', 'sg']}
                                        enableSearch={true}
                                        searchPlaceholder='Search countries...'
                                    />
                                    {formErrors.phone && <span className='error-message'>{formErrors.phone}</span>}
                                </div>

                                <div className='improved-form-group'>
                                    <label htmlFor='email'>Email Address (Optional)</label>
                                    <input
                                        type='email'
                                        id='email'
                                        name='email'
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={formErrors.email ? 'error' : ''}
                                        placeholder='your.email@example.com (optional)'
                                    />
                                    {formErrors.email && <span className='error-message'>{formErrors.email}</span>}
                                </div>
                            </div>

                            <div className='improved-form-group'>
                                <label htmlFor='message'>Message (Optional)</label>
                                <textarea
                                    id='message'
                                    name='message'
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows='3'
                                    placeholder='Tell us about your dental concerns or ask any questions...'
                                ></textarea>
                            </div>

                            <button type='submit' className='improved-submit-button' disabled={isSubmitting}>
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
            <button className="floating-cta phone visible" onClick={handlePhoneButtonClick} aria-label='Call now'>
                <FaPhone /> {/* You can replace FaPhone with any icon you want */}
            </button>
            <button className="floating-cta whatsapp visible" onClick={handleWhatsappButtonClick} aria-label='Book appointment'>
                <FaWhatsapp />
            </button>
        </div>
    );
};

export default DentalServicePage;

// ${serviceName.toLowerCase().replace(/\s+/g, '-')}-what.jpg`} a
// ${serviceName.toLowerCase().replace(/\s+/g, '-')}-when.jpg`} a
// ${serviceName.toLowerCase().replace(/\s+/g, '-')}-how.jpg`} al
// ${serviceName.toLowerCase().replace(/\s+/g, '-')}-benefits.jpg
// ${serviceName.toLowerCase().replace(/\s+/g, '-')}-procedure.jp
